/**
 * Pure decision module for the OS-permission foreground sync.
 *
 * The user can change push permission any time via iOS/Android
 * Settings, and neither OS fires an app-level event when they do.
 * The pattern is: on every foreground, check the current OS
 * permission, compare to what we last observed, act on the change.
 *
 * Cases we handle:
 *   - was granted + we have a server token, now still granted: no-op.
 *   - was granted + we have a server token, now denied: user turned
 *     it off in Settings. Revoke server-side (drop the token, record
 *     PushPromptState "system_denied") so the app reflects reality
 *     and no futile push attempts get made.
 *   - was denied, now granted: user re-enabled in Settings. Silently
 *     register a new token so future pushes work. Re-record
 *     PushPromptState as "granted".
 *   - Any state, but the current OS state matches what the server
 *     thinks (hasToken iff granted): no-op.
 *
 * Pure module: no React, no fetch, no Capacitor. Testable from root
 * vitest.
 */

export type OsPermissionState = "granted" | "denied" | "prompt";

export type OsSyncInput = {
  /** Current OS permission (via Capacitor's checkPermissions on foreground). */
  currentOsPermission: OsPermissionState;
  /** Whether the server has a PushToken row for (userId, venueId) right now. */
  serverHasToken: boolean;
};

export type OsSyncAction =
  | "no_action"
  | "revoke_token" // OS turned off in Settings; drop the server token + record denial
  | "reregister"; // OS turned on in Settings; silently register a new token

export type OsSyncDecision = {
  action: OsSyncAction;
  reason: string;
};

/**
 * Decide what the foreground sync hook should do.
 *
 * The truth table collapses to two mismatches worth acting on:
 *   OS granted + no server token   -> reregister
 *   OS denied  + server has token  -> revoke
 * Everything else is a no-op (aligned, or OS is still "prompt" which
 * means the user hasn't given us a definitive answer via Settings).
 */
export function decideOsPermissionSync(input: OsSyncInput): OsSyncDecision {
  const { currentOsPermission, serverHasToken } = input;

  if (currentOsPermission === "granted" && !serverHasToken) {
    return {
      action: "reregister",
      reason: "os_granted_but_server_missing_token",
    };
  }
  if (currentOsPermission === "denied" && serverHasToken) {
    return {
      action: "revoke_token",
      reason: "os_denied_but_server_still_has_token",
    };
  }
  // 'prompt' state is only meaningful pre-first-ask. If we ever see
  // it while a token exists (shouldn't happen normally), treat as
  // no-op rather than deleting a working registration.
  return { action: "no_action", reason: "aligned" };
}
